/** @module AuthAnalyticsBase */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */

import {
  SURFACE_AUTH,
  FLOW_SIGN_IN,
  FLOW_PASSWORD_RESET,
} from './auth-analytics-constants.mjs';

/**
 * Base class for analytics providers
 * Defines the contract that all analytics providers must implement
 * Also provides shared utility methods used by all providers
 *
 * NOTE: This is an internal implementation detail.
 * Consumers should import from auth-analytics.mjs instead.
 */
class AuthAnalyticsBase {
  constructor(options = {}) {
    this._options = options;
  }

  /**
   * Initialize the analytics provider
   * Abstract method - must be implemented by subclasses
   * @param {Object} _options - Configuration options
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async initialize(_options = {}) {
    // No-op implementation - subclasses should override
    await Promise.resolve();
  }

  /**
   * Track an event
   * Abstract method - must be implemented by subclasses
   * @param {string} _eventName - Name of the event to track
   * @param {object} _properties - Event properties
   * @param {object} _context - Event context
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line no-unused-vars
  async track(_eventName, _properties = {}, _context = {}) {
    // No-op implementation - subclasses should override
    await Promise.resolve();
  }

  /**
   * Helper to build standard auth context object
   * @param {object} options - Context options (accepts any custom properties)
   * @param {string} options.surface - UI surface (e.g., SURFACE_AUTH)
   * @param {string} options.flow - User flow (e.g., FLOW_SIGN_IN, FLOW_PASSWORD_RESET)
   * @param {string} options.step - Current step in flow (e.g., STEP_LOGIN_PAGE, STEP_*)
   * @param {string} [options.clientId] - Auth0 client ID
   * @param {string} [options.tenant] - Auth0 tenant
   * @param {string} [options.tenantClass] - Tenant class (e.g., 'shipstation', 'shipstation-partners')
   * @param {string} [options.appName] - Application name
   * @returns {object} Context object with all provided properties
   */
  buildAuthContext(options = {}) {
    const context = {
      surface: options.surface || 'auth',
      flow: options.flow || 'unknown',
      step: options.step || 'unknown',
    };

    // Add optional standard properties
    if (options.clientId) {
      context.client_id = options.clientId;
    }
    if (options.tenant) {
      context.tenant = options.tenant;
    }
    if (options.tenantClass) {
      context.tenant_class = options.tenantClass;
    }
    if (options.appName) {
      context.app_name = options.appName;
    }

    // Spread any additional custom properties that weren't explicitly handled
    /* eslint-disable no-unused-vars */
    const {
      surface: _surface,
      flow: _flow,
      step: _step,
      clientId: _clientId,
      tenant: _tenant,
      tenantClass: _tenantClass,
      appName: _appName,
      ...customProps
    } = options;
    /* eslint-enable no-unused-vars */
    Object.assign(context, customProps);

    return context;
  }

  /**
   * Helper to build sign-in context (pre-populates surface and flow)
   * @param {object} options - Context options (accepts any custom properties)
   * @param {string} options.step - Current step in sign-in flow
   * @param {string} [options.clientId] - Auth0 client ID
   * @param {string} [options.tenant] - Auth0 tenant
   * @param {string} [options.tenantClass] - Tenant class (e.g., 'shipstation', 'shipstation-partners')
   * @param {string} [options.appName] - Application name
   * @returns {object} Context object with all provided properties
   */
  buildSigninContext(options = {}) {
    return this.buildAuthContext({
      surface: SURFACE_AUTH,
      flow: FLOW_SIGN_IN,
      ...options, // Spread all properties including custom ones
    });
  }

  /**
   * Helper to build password reset context (pre-populates surface and flow)
   * @param {object} options - Context options (accepts any custom properties)
   * @param {string} options.step - Current step in password reset flow
   * @param {string} [options.clientId] - Auth0 client ID
   * @param {string} [options.tenant] - Auth0 tenant
   * @param {string} [options.tenantClass] - Tenant class (e.g., 'shipstation', 'shipstation-partners')
   * @param {string} [options.appName] - Application name
   * @returns {object} Context object with all provided properties
   */
  buildPasswordResetContext(options = {}) {
    return this.buildAuthContext({
      surface: SURFACE_AUTH,
      flow: FLOW_PASSWORD_RESET,
      ...options, // Spread all properties including custom ones
    });
  }

  /**
   * Helper to register text entry tracking on blur events
   * Handles blur event registration and empty check, then calls provided callback
   * @param {HTMLElement} element - The input element to track
   * @param {Function} onTextEnteredCallback - Callback to invoke when non-empty text is entered
   * @param {Object} options - Optional configuration
   * @param {HTMLElement} options.alsoCheckOn - Optional button element to also check on click (handles autofill + immediate click)
   */
  registerTextEntryTracking(element, onTextEnteredCallback, options = {}) {
    const { alsoCheckOn = null } = options;
    let hasTracked = false;

    const trackIfNotEmpty = () => {
      if (element.value && element.value.trim().length > 0 && !hasTracked) {
        hasTracked = true;
        onTextEnteredCallback();
      }
    };

    element.addEventListener('blur', trackIfNotEmpty);

    if (alsoCheckOn) {
      alsoCheckOn.addEventListener('click', trackIfNotEmpty);
    }
  }

  /**
   * Helper to register checkbox tracking on change events
   * Handles change event registration, then calls provided callback with checked state
   * @param {HTMLElement} element - The checkbox element to track
   * @param {Function} onCheckboxChangedCallback - Callback to invoke when checkbox changes, receives checked state
   */
  registerCheckboxEvent(element, onCheckboxChangedCallback) {
    element.addEventListener('change', () => {
      onCheckboxChangedCallback(element.checked);
    });
  }

  /**
   * Helper to register button click tracking
   * Handles click event registration, then calls provided callback
   * @param {HTMLElement} element - The button element to track
   * @param {Function} onButtonClickedCallback - Callback to invoke when button is clicked
   */
  registerButtonClickEvent(element, onButtonClickedCallback) {
    element.addEventListener('click', () => {
      onButtonClickedCallback();
    });
  }
}

export default AuthAnalyticsBase;
